import { type FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LanguageSelect } from '../components/LanguageSelect';
import { postJSON, postLogout, type LoginResponse, type MessageResponse } from '../lib/api';
import { sha256Utf8Hex } from '../lib/passwordWire';
import { authCardClass, pageWrapClass } from '../layout/page';
import { inputStackFieldClass, primaryButtonBlockClass } from '../components/form/fieldClasses';

type View = 'login' | 'mfa' | 'change';

export default function UserLoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [view, setView] = useState<View>('login');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [mfaToken, setMfaToken] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [pendingMustChange, setPendingMustChange] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [msg, setMsg] = useState('');
  const [msgOk, setMsgOk] = useState(false);
  const [busy, setBusy] = useState(false);

  function resetMessage() {
    setMsg('');
    setMsgOk(false);
  }

  async function onLogin(e: FormEvent) {
    e.preventDefault();
    resetMessage();
    setBusy(true);
    try {
      const { res, data } = await postJSON<LoginResponse>('/api/user/login', {
        username: username.trim(),
        password: await sha256Utf8Hex(password),
      });
      if (!res.ok) {
        setMsg(data.error || t('common.error'));
        return;
      }
      if (data.mfa_required && data.mfa_token) {
        setMfaToken(data.mfa_token);
        setPendingMustChange(!!data.must_change_password);
        setMfaCode('');
        setView('mfa');
        return;
      }
      if (data.must_change_password) {
        setView('change');
        return;
      }
      navigate('/home', { replace: true });
    } finally {
      setBusy(false);
    }
  }

  async function onMfa(e: FormEvent) {
    e.preventDefault();
    resetMessage();
    setBusy(true);
    try {
      const { res, data } = await postJSON<LoginResponse>('/api/user/login/mfa', {
        mfa_token: mfaToken,
        mfa_code: mfaCode.trim(),
      });
      if (!res.ok) {
        setMsg(data.error || t('common.error'));
        return;
      }
      if (pendingMustChange || data.must_change_password) {
        setView('change');
        return;
      }
      navigate('/home', { replace: true });
    } finally {
      setBusy(false);
    }
  }

  async function onChangePassword(e: FormEvent) {
    e.preventDefault();
    resetMessage();
    setBusy(true);
    try {
      const { res, data } = await postJSON<MessageResponse>('/api/user/change-password', {
        new_password: await sha256Utf8Hex(newPassword),
      });
      if (!res.ok) {
        setMsg(data.error || t('common.error'));
        return;
      }
      await postLogout();
      setMsgOk(true);
      setMsg(data.message || t('user.changedOk'));
      setView('login');
      setPassword('');
      setNewPassword('');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={pageWrapClass}>
      <main className={authCardClass}>
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-xl font-semibold tracking-tight text-slate-900">{t('user.title')}</h1>
          <LanguageSelect />
        </div>

        {view === 'mfa' && (
          <form className="grid gap-3" onSubmit={(e) => void onMfa(e)}>
            <p className="text-sm text-slate-600">{t('mfa.loginHint')}</p>
            <input
              required
              inputMode="numeric"
              autoComplete="one-time-code"
              className={inputStackFieldClass}
              placeholder={t('mfa.codePh')}
              value={mfaCode}
              onChange={(e) => setMfaCode(e.target.value)}
            />
            <button type="submit" disabled={busy} className={primaryButtonBlockClass}>
              {t('user.login')}
            </button>
            <button
              type="button"
              disabled={busy}
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 hover:bg-slate-50"
              onClick={() => {
                setView('login');
                setMfaToken('');
                setMfaCode('');
                resetMessage();
              }}
            >
              {t('mfa.back')}
            </button>
          </form>
        )}

        {view === 'login' && (
          <form className="grid gap-3" onSubmit={(e) => void onLogin(e)}>
            <input
              required
              autoComplete="username"
              className={inputStackFieldClass}
              placeholder={t('user.username')}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
            <input
              required
              type="password"
              autoComplete="current-password"
              className={inputStackFieldClass}
              placeholder={t('user.password')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <button type="submit" disabled={busy} className={primaryButtonBlockClass}>
              {t('user.login')}
            </button>
          </form>
        )}

        {view === 'change' && (
          <form className="grid gap-3" onSubmit={(e) => void onChangePassword(e)}>
            <p className="text-sm text-slate-600">{t('user.mustChange')}</p>
            <input
              required
              type="password"
              autoComplete="new-password"
              className={inputStackFieldClass}
              placeholder={t('user.newPassword')}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
            <button type="submit" disabled={busy} className={primaryButtonBlockClass}>
              {t('user.updatePwd')}
            </button>
          </form>
        )}

        {msg ? (
          <p className={`mt-4 min-h-5 text-sm ${msgOk ? 'text-emerald-700' : 'text-red-600'}`}>{msg}</p>
        ) : (
          <p className="mt-4 min-h-5 text-sm" />
        )}
      </main>
    </div>
  );
}
